import {ILocationGridItem, ILookupValue} from '../../model';
import { ILocationFilter, ILocationItem, ILocationUpsert } from '../../model/api/locations';
import { ILocation } from '../../model/api/locations/location';
import { getApi } from './api';
import {State as DataState} from "@progress/kendo-data-query/dist/npm/state";
import {buildSearchQueryString} from "../../utils/kendo-query-builder";


interface KendoDataResult {
  data: Array<ILocationGridItem>;
  total: number;
}


const URL = '/location';

const getList = async (filter: ILocationFilter): Promise<{ data: ILocationItem[]; total: number }> => {
  let url = `${URL}?skip=${filter.skip}&take=${filter.take}`;

  if (filter.customers?.length) {
    url += `&${filter.customers?.map((x) => 'customers=' + x).join('&')}`;
  }

  if (filter.types?.length) {
    url += `&${filter.types?.map((x) => 'types=' + x).join('&')}`;
  }

  if (filter.query?.length) {
    url += `&query=${filter.query}`;
  }

  if (filter.enabled !== undefined) {
    url += `&enabled=${filter.enabled}`;
  }

  const api = await getApi();
  return api
    .get<{ data: ILocationItem[]; total: number }>(url)
    .then((response) => response.data)
    .then((res) => {
      res.data.forEach((x) => (x.updatedAt = new Date(x.updatedAt)));
      return res;
    });
};

const getListByCustomerId = async (customerId: number) => {
  const url = `${URL}/by-customer?id=${customerId}`;
  const api = await getApi();
  return api.get<ILookupValue[]>(url).then((response) => response.data || []);
};

const get = async (id: number) => {
  const api = await getApi();
  return api.get<ILocation>(`${URL}/${id}`).then((response) => response.data);
};

const remove = async (ids: number[]) => {
  const idsParams = ids.map((x) => `id=${x}`).join('&');
  const api = await getApi();
  return api.delete<void>(`${URL}?${idsParams}`).then((response) => response.data);
};

const create = async (location: ILocationUpsert) => {
  const api = await getApi();
  return api.post<{ id: number }>(`${URL}`, location).then((response) => response.data.id);
};

const update = async (id: number, location: ILocationUpsert) => {
  const api = await getApi();
  return api.put<void>(`${URL}/${id}`, location).then((response) => response.data);
};

const deactivate = async (id: number) => {
  const api = await getApi();
  return api
    .put<void>(`${URL}/${id}/deactivate`)
    .then((response) => response.data);
};

const getListForGrid = async (
    dataState: DataState,
    query: string | undefined | null
): Promise<{ data: ILocationGridItem[], total: number }> => {
  const queryString = buildSearchQueryString(dataState, query);

  const api = await getApi();
  return api
      .get<KendoDataResult>(`${URL}/kendo-grid-list?${queryString}`)
      .then((response) => response.data)
      .then((res) => {
        return {
          data: res.data,
          total: res.total,
        };
      });
};

export const locationsService = {
  getList,
  getListByCustomerId,
  getListForGrid,
  get,
  remove,
  create,
  update,
  deactivate
};
